import React, { FC, useRef } from 'react';
import styled from 'styled-components';
import { Upload, Empty, Alert } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

interface IStep1FileUploadProps {
  selectedFile: File | undefined;
  analysisResult: any;
  isLoading: boolean;
  error: string | undefined;
  onFileSelect: (file: File) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 0;
`;

const Description = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
`;

const UploadZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  border: 2px dashed ${({ theme }) => theme.colors.primary.primary};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.primary.primaryContainer};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.primaryHover};
  }
`;

const UploadIcon = styled(UploadOutlined)`
  font-size: 32px;
  color: ${({ theme }) => theme.colors.primary.primary};
  margin-bottom: 12px;
`;

const UploadText = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 0 0 4px 0;
`;

const UploadSubtext = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
`;

const AnalysisSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 4px;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.primary};
`;

const StatLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

const StatValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

const FileName = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 8px 0 0 0;
`;

const Step1FileUpload: FC<IStep1FileUploadProps> = ({
  selectedFile,
  analysisResult,
  isLoading,
  error,
  onFileSelect,
}) => {
  const uploadRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      onFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Container>
      <Header>
        <Title>Selecione um arquivo BCF</Title>
        <Description>Arraste e solte o arquivo BCF aqui ou clique para selecionar</Description>
      </Header>

      {error && <Alert message={error} type="error" showIcon />}

      {!selectedFile || !analysisResult ? (
        <UploadZone
          ref={uploadRef}
          onDrop={handleDrop}
          onDragOver={(e: React.DragEvent<HTMLDivElement>) => e.preventDefault()}
          onClick={handleClick}
        >
          <UploadIcon />
          <UploadText>Clique ou arraste o arquivo BCF</UploadText>
          <UploadSubtext>Suporte para arquivos .bcf e .bcfzip até 100MB</UploadSubtext>
          <input
            ref={fileInputRef}
            type="file"
            accept=".bcf,.bcfzip"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files?.[0]) {
                onFileSelect(e.target.files[0]);
              }
            }}
            id="bcf-file-input"
          />
        </UploadZone>
      ) : null}

      {analysisResult && (
        <AnalysisSection>
          <Title style={{ marginTop: 0 }}>Análise do arquivo</Title>
          <StatCard>
            <StatLabel>Arquivo selecionado</StatLabel>
            <FileName>{selectedFile?.name}</FileName>
          </StatCard>
          <StatCard>
            <StatLabel>Total de tópicos</StatLabel>
            <StatValue>{analysisResult.bcfValues.totalTopics || 0} Tópicos</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Tipos detectados</StatLabel>
            <StatValue>{analysisResult.bcfValues.types?.length || 0} Tipos</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Status detectados</StatLabel>
            <StatValue>{analysisResult.bcfValues.statuses?.length || 0} Status</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Prioridades detectadas</StatLabel>
            <StatValue>{analysisResult.bcfValues.priorities?.length || 0} Prioridades</StatValue>
          </StatCard>
          {analysisResult.bcfValues.labels?.length > 0 && (
            <StatCard>
              <StatLabel>Etiquetas detectadas</StatLabel>
              <StatValue>{analysisResult.bcfValues.labels.length} Etiquetas</StatValue>
            </StatCard>
          )}
          <StatCard style={{ borderLeftColor: analysisResult.bcfValues.totalViewpoints > 0 ? '#10b981' : undefined }}>
            <StatLabel>📷 Viewpoints (Pontos de Vista 3D)</StatLabel>
            {analysisResult.bcfValues.totalViewpoints > 0 ? (
              <StatValue style={{ color: '#10b981' }}>
                {analysisResult.bcfValues.totalViewpoints} viewpoint{analysisResult.bcfValues.totalViewpoints !== 1 ? 's' : ''} em {analysisResult.bcfValues.topicsWithViewpoints} tópico{analysisResult.bcfValues.topicsWithViewpoints !== 1 ? 's' : ''}
              </StatValue>
            ) : (
              <StatValue style={{ color: '#9ca3af' }}>
                Nenhum viewpoint encontrado
              </StatValue>
            )}
          </StatCard>
        </AnalysisSection>
      )}
    </Container>
  );
};

export default Step1FileUpload;
